import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Mic2, Swords, Trophy, Plus, LogIn, LogOut, User, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/battle", label: "Battle", icon: Swords, testid: "nav-battle" },
  { to: "/comedians", label: "Comedians", icon: Mic2, testid: "nav-comedians" },
  { to: "/submit", label: "Submit", icon: Plus, testid: "nav-submit" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      {/* Top marquee */}
      <div className="relative overflow-hidden border-b border-white/5 bg-black/40 backdrop-blur-sm">
        <div className="marquee-track flex whitespace-nowrap py-1.5 text-[10px] font-mono-ui uppercase tracking-[0.35em] text-amber-400/70">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0 items-center gap-8 px-6">
              <span>Live Rankings</span><Zap className="h-3 w-3" />
              <span>Head-to-Head Battles</span><Zap className="h-3 w-3" />
              <span>Rank the Kings & Queens of Comedy</span><Zap className="h-3 w-3" />
              <span>Vote. Laugh. Share.</span><Zap className="h-3 w-3" />
              <span>ELO-Powered</span><Zap className="h-3 w-3" />
              <span>Since 2026</span><Zap className="h-3 w-3" />
            </div>
          ))}
        </div>
      </div>

      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-md bg-amber-400 text-black glow-amber">
              <Mic2 className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-2xl text-white tracking-wider">INSTA LAUGH</span>
              <span className="text-[9px] font-mono-ui uppercase tracking-[0.3em] text-amber-400/80">Comedy Ranked</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-amber-400/10 text-amber-400"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <l.icon className="h-4 w-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/profile"
                  data-testid="profile-link"
                  className="hidden sm:flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 hover:border-amber-400/60 hover:bg-amber-400/10 transition"
                >
                  <User className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-medium text-white" data-testid="user-name">{user.name}</span>
                </Link>
                <Button
                  data-testid="logout-btn"
                  variant="ghost"
                  size="sm"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="text-slate-300 hover:text-white hover:bg-white/5"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="login-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/login")}
                  className="text-slate-300 hover:text-white"
                >
                  <LogIn className="h-4 w-4 mr-1" />
                  Sign in
                </Button>
                <Button
                  data-testid="register-btn"
                  size="sm"
                  onClick={() => navigate("/register")}
                  className="bg-amber-400 text-black hover:bg-amber-300 font-bold btn-brutal"
                >
                  Join Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10">
        <div className="grid grid-cols-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 text-[10px] font-mono-ui uppercase tracking-widest ${
                  isActive ? "text-amber-400" : "text-slate-400"
                }`
              }
            >
              <l.icon className="h-5 w-5" />
              {l.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
}
